import Vue from "vue";
import deepmerge from "deepmerge";
import set from "lodash/set";
import get from "lodash/get";
import { _isMobile } from "../utils/helpers";
//import store from "@/store";
import * as ERRORES from "../../config/constants/step-constants";

const isProduction = PRODUCTION;

const _normalizeCharacters = str => {
  return str
    ? (str + "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
    : str;
};

const AdobeFunctions = {
  internal(context, _callback) {
    AdobeFunctions.clearVars();
    AdobeFunctions.setConstants();
    AdobeFunctions.setOptimizations(context);
    setTimeout(_callback, 0);
  },
  callAdobeDTM(method, event) {
    /**
     * Realizar llamada al DTM
     */
    if (event !== "Page View" && event !== "Internal Link") {
      // No se debe reportar los props de application para page views
      AdobeFunctions.setApplicationProps();
    }
    try {
      window[method](event);
      // Resetear flag de pageview automático
      window._pageViewAutomatico = false;
    } catch (error) {
      Vue.$log.error("🚀 Adobe Launch error: ", error);
    }
    Vue.$log.info("🚀 Adobe Launch call: ", event);
  },
  clearVars() {
    /**
     * Limpiar las variables cuando hace falta
     */
    set(window.digitalData, "products.attributes", []);
    set(window.digitalData, "internalCampaign.attributes", []);
    set(window.digitalData, "application.state", "");
    set(window.digitalData, "application.step", "");
    set(window.digitalData, "application.isQualifiedVisits", "");
    set(window.digitalData, "application.errorType", "");
    set(window.digitalData, "page.pageInfo.errorPage", "");
    set(window.digitalData, "page.pageInfo.level2", "");
    set(window.digitalData, "page.pageInfo.pageName", "");
    set(window.digitalData, "page.pageActivity.link.name", "");
    set(window.digitalData, "application.typology", "");
    set(window.digitalData, "application.process", "");
    set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
    set(window.digitalData, "user.segment.profile", "");
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "",
          name: ""
        },
        fulfillmentModel: "",
        globalApplication: ""
      })
    );
  },
  setPageInstanceId() {
    /**
     * Establecer entorno en datalayer
     */
    set(window.digitalData, "pageInstanceID", isProduction ? "pro" : "de");
  },
  setIsMobile() {
    if (_isMobile()) {
      set(window.digitalData, "user.device.mobile", "si");
    } else {
      set(window.digitalData, "user.device.mobile", "no");
    }
  },
  setSysEnv() {
    if (_isMobile()) {
      set(window.digitalData, "page.pageInfo.sysEnv", "web-movil");
    } else {
      set(window.digitalData, "page.pageInfo.sysEnv", "escritorio");
    }
  },
  setPageName(pageName) {
    let segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      "publica",
      "general",
      "pymes",
      "precalificacion"
    ];
    set(window.digitalData, "page.pageInfo.level2", "precalificacion");
    if (pageName) {
      segments.push(pageName);
      set(window.digitalData, "page.pageInfo.level3", pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
  },
  setConstants() {
    set(window.digitalData, "page.pageInfo.server", window.location.hostname);
    set(window.digitalData, "user.device.userAgent", navigator.userAgent);
    set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
    AdobeFunctions.setPageInstanceId();
    AdobeFunctions.setIsMobile();
    AdobeFunctions.setSysEnv();
    AdobeFunctions.setPageName();
  },
  setOptimizations(context) {
    if (context && context.state && context.state.adobeTargetExperience) {
      /**
       * Se especificó una experiencia de Target. Indicarla al tagging.
       * La experiencia DEBE venir de Target con el formato: `IDACTIVIDAD_EXPERIENCIA`
       * Ej: "AR072019_B"
       */
      let datosTarget = context.state.adobeTargetExperience.split("_");
      if (datosTarget.length === 2) {
        let attributes = [
          {
            idOptimization: datosTarget[0],
            experience: datosTarget[1],
            place: "",
            type: "",
            executor: "Target",
            audience: ""
          }
        ];
        set(window.digitalData, "optimization.attributes", attributes);
      }
    }
  },
  setInternalCampaign() {
    let attributes = [
      {
        location: "",
        campaignFormat: "",
        collectiveCode: "",
        campaignName: "",
        product: "",
        productCode: "",
        quantity: ""
      }
    ];
    set(window.digitalData, "internalCampaign.attributes", attributes);
  },
  setProductAttributes(context) {
    let attributes = [];
    let attributesModel = {
      primaryCategory: "",
      productSubtype: "",
      productName: "",
      productCode: "",
      quantity: "",
      amount: "",
      paymentAmount: "",
      numberOfPayments: "",
      paymentDate: "",
      paymentType: "",
      serviceCharge: "",
      currency: "",
      numberOfHolders: "",
      interestRate: {
        tin: "",
        tae: "",
        rate: ""
      },
      term: "",
      group: "",
      state: ""
    };
    const __addProduct = (producto, amount = "") => {
      if (get(producto, "productName", "")) {
        attributes.push(
          deepmerge(attributesModel, {
            primaryCategory: get(producto, "primaryCategory", ""),
            productSubtype: get(producto, "productSubtype", ""),
            productName: get(producto, "productName", ""),
            amount: String(amount || "")
          })
        );
      }
    };
    __addProduct(
      {
        primaryCategory: "tarjetas",
        productSubtype: "tarjetas credito",
        productName: "visa business"
      },
      get(context, "state.reactivo.paso1.values.visaBusiness")
    );
    __addProduct(
      {
        primaryCategory: "prestamos",
        productName: "acuerdo en cuenta corriente"
      },
      get(context, "state.reactivo.paso1.values.cuentaCorriente")
    );
    __addProduct(
      {
        primaryCategory: "prestamos",
        productName: "prestamo online"
      },
      get(context, "state.reactivo.paso1.values.prestamoFinanciero")
    );
    __addProduct(
      {
        primaryCategory: "prestamos",
        productName: "descuento de cheques"
      },
      get(context, "state.reactivo.paso1.values.descuentoCheques")
    );

    set(window.digitalData, "products.attributes", attributes);
  },
  setProductState(state) {
    get(window.digitalData, "products.attributes", []).forEach(
      (product, index) => {
        set(window.digitalData.products.attributes, `[${index}].state`, state);
      }
    );
  },
  setApplicationProps() {
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "contratacion",
          name: `contacto empresa`
        },
        fulfillmentModel: "offline"
      })
    );
    // en reactivo aun no se usa el origen
    /*set(
      window.digitalData,
      "application.process",
      _normalizeCharacters(store.state.origen)
    );*/
    set(
      window.digitalData,
      "application.globalApplication",
      "company prequalification"
    );
  }
};

const Adobe = {
  enable() {},
  wait() {
    return !!window._satellite && !!(document.readyState === "complete");
  },
  init(context) {
    AdobeFunctions.internal(context, () => {
      Vue.$log.info("🚀 Adobe Launch working");
    });
  },
  appStartedClick(context) {
    AdobeFunctions.internal(context, () => {
      set(
        window.digitalData,
        "application.step",
        _normalizeCharacters(context.mutation.payload)
      );
      AdobeFunctions.setPageName("1 identificacion empresa");
      set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
      AdobeFunctions.callAdobeDTM("digitalLink", "App Started");
    });
  },
  appOnClickStart(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      set(
        window.digitalData,
        "application.step",
        _normalizeCharacters(context.mutation.payload)
      );
      AdobeFunctions.setPageName();
      AdobeFunctions.setApplicationProps();
      AdobeFunctions.callAdobeDTM("digitalLink", "App On Click Start");
    });
  },
  internalLink(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
      set(window.digitalData, "page.pageInfo.level3", "");
      set(
        window.digitalData,
        "page.pageActivity.link.name",
        get(context.mutation.payload, "name", "")
      );
      AdobeFunctions.setPageName(get(context.mutation.payload, "namePage", ""));
      AdobeFunctions.callAdobeDTM("digitalLink", "Internal Link");
    });
  },
  abrirCuenta(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
      set(window.digitalData, "page.pageInfo.level1", "pymes");
      set(window.digitalData, "page.pageInfo.level2", "precalificacion");
      set(window.digitalData, "page.pageInfo.level3", "pagina exitosa");
      set(
        window.digitalData,
        "page.pageActivity.link.name",
        get(context.mutation.payload, "name", "")
      );
      AdobeFunctions.setPageName(get(context.mutation.payload, "namePage", ""));
      AdobeFunctions.callAdobeDTM("digitalLink", "External Link");
    });
  },
  pageView(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      AdobeFunctions.setPageName(context.mutation.payload);
      AdobeFunctions.callAdobeDTM("digitalView", "Page View");
    });
  },
  paso1(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("1 identificacion empresa");
      set(window.digitalData, "application.step", "1 identificacion empresa");
      set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
      set(window.digitalData, "application.isQualifiedVisits", false);
      AdobeFunctions.callAdobeDTM("digitalView", "App Page Visit");
    });
  },
  oferta(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("2 distribucion credito");
      set(window.digitalData, "application.step", "2 distribucion credito");
      set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
      set(window.digitalData, "page.pageActivity.link.name", "");
      AdobeFunctions.callAdobeDTM("digitalView", "App Step 2");
    });
  },
  gracias(context) {
    AdobeFunctions.internal(context, () => {
      if (!context.state.isExceeded) {
        AdobeFunctions.setPageName("pagina exitosa");
        set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
        set(window.digitalData, "application.step", "pagina exitosa");
        set(window.digitalData, "application.state", "aprobado");
        AdobeFunctions.setProductAttributes(context);
        AdobeFunctions.setProductState("aprobado");
        AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
      } else {
        AdobeFunctions.setPageName("pagina exitosa");
        set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
        set(window.digitalData, "application.step", "pagina exitosa");
        set(window.digitalData, "application.state", "rechazado");
        set(window.digitalData, "user.segment.profile", "facturacion maxima");
        AdobeFunctions.setProductAttributes(context);
        set(
          window.digitalData,
          "products.attributes[2].productName",
          "prestamo financiero empresas"
        );
        AdobeFunctions.setProductState("rechazado");
        AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
      }
    });
  },
  errorOferta(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("pagina exitosa");
      set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
      set(window.digitalData, "application.step", "pagina exitosa");
      set(window.digitalData, "application.state", "rechazado");
      set(window.digitalData, "user.segment.profile", "no es posible avanzar");
      AdobeFunctions.setProductAttributes(context);
      AdobeFunctions.setProductState("rechazado");
      AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
    });
  },
  mensaje(context) {
    AdobeFunctions.internal(context, () => {
      // Para los errores se disparan eventos diferentes
      // de acuerdo al error por lo que en cada case debe llamarse a adobeDTM
      // Huella avanzada: errores que puede volver
      // app completed: finaliza el proceso y no puede volver
      AdobeFunctions.clearVars();
      switch (context.mutation.payload.params.mje) {
        case ERRORES.INDIVIDUO_NEGATIVO:
          AdobeFunctions.setPageName("pagina exitosa");
          set(window.digitalData, "page.pageInfo.level2", "precalificacion");
          set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
          set(window.digitalData, "application.step", "pagina exitosa");
          set(window.digitalData, "application.state", "rechazado");
          set(
            window.digitalData,
            "user.segment.profile",
            "empresa ya simulada"
          );
          AdobeFunctions.setProductAttributes(context);
          AdobeFunctions.setProductState("rechazado");
          AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
          break;

        case ERRORES.CLIENTE_ENCONTRADO:
          AdobeFunctions.setPageName("pagina exitosa");
          set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
          set(window.digitalData, "application.step", "pagina exitosa");
          set(window.digitalData, "application.state", "rechazado");
          set(window.digitalData, "user.segment.profile", "empresa cliente");
          AdobeFunctions.setProductAttributes(context);
          AdobeFunctions.setProductState("rechazado");
          AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
          break;

        case ERRORES.CLIENTE_FINANCIADO:
          AdobeFunctions.setPageName("pagina exitosa");
          set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
          set(window.digitalData, "application.step", "pagina exitosa");
          set(window.digitalData, "application.state", "rechazado");
          set(
            window.digitalData,
            "user.segment.profile",
            "empresa con calificacion"
          );
          AdobeFunctions.setProductAttributes(context);
          AdobeFunctions.setProductState("rechazado");
          AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
          break;

        default:
          AdobeFunctions.setPageName("error tecnico");
          set(window.digitalData, "page.pageInfo.errorPage", "error tecnico");
          AdobeFunctions.callAdobeDTM("digitalView", "Page View");
          break;
      }
    });
  }
};

export default Adobe;
